import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  type Ref,
} from 'react';
import { cn } from '@/utils/cn';

const buttonVariants = cva(
  [
    'flex items-center justify-center gap-1.5 rounded-[14px] border-4 border-transparent text-center font-semibold transition-all duration-150',
    'cursor-pointer',
    // Snappy, sporty press feedback
    'active:scale-[0.97]',
    'disabled:cursor-not-allowed disabled:bg-slate-400 disabled:opacity-50 disabled:active:scale-100',
    // Enhanced focus styles for accessibility
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-light-blue focus-visible:ring-offset-2 focus-visible:ring-offset-dark-gray',
    // Ensure interactive states are accessible
    'select-none',
  ],
  {
    variants: {
      variant: {
        default: 'bg-light-gray text-white hover:bg-light-gray/70',
        none: 'border-blue-gray border-solid bg-component-bg text-blue-gray hover:bg-blue-gray/5',
        primary:
          'border-light-blue border-solid bg-light-blue text-black hover:border-light-blue/90 hover:bg-light-blue/90',
        accent:
          'border-red border-solid bg-red text-white hover:bg-red/90 hover:shadow-[0_0_24px_rgba(239,43,59,0.5)]',
        secondary:
          'border-blue-gray border-solid bg-component-bg text-blue-gray hover:bg-blue-gray/5',
        ghost: 'hover:bg-light-gray',
        outline:
          'border-blue-gray border-solid text-blue-gray hover:bg-blue-gray/10',
        outlineWithBg:
          'border-blue-gray border-solid bg-medium-gray text-blue-gray hover:bg-blue-gray/10',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-[42px] px-4 text-sm',
        lg: 'h-12 px-6 text-base',
      },
      square: {
        true: '',
        false: '',
      },
      glow: {
        true: 'bg-gradient-to-b from-light-blue/90 via-light-blue to-light-blue/80 text-white shadow-[0_0_40px_rgba(79,169,255,0.6),inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_4px_rgba(0,0,0,0.3)] transition-all duration-300 hover:from-light-blue hover:to-light-blue/85 hover:shadow-[0_0_60px_rgba(79,169,255,0.8),inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_-2px_4px_rgba(0,0,0,0.4)]',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
    compoundVariants: [
      {
        size: 'sm',
        square: true,
        class: 'size-8 p-0',
      },
      {
        size: 'md',
        square: true,
        class: 'size-9 p-0',
      },
      {
        size: 'lg',
        square: true,
        class: 'size-12 p-0',
      },
    ],
  }
);

type ButtonBaseProps = VariantProps<typeof buttonVariants> & {
  // Accessibility props
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-pressed'?: boolean | 'mixed';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-current'?: boolean | 'page' | 'step' | 'location' | 'date' | 'time';
};

type ButtonAsButtonProps = ButtonBaseProps & {
  as?: 'button';
  href?: never;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps>;

type ButtonAsLinkProps = ButtonBaseProps & {
  as: 'link';
  href: string;
} & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    keyof ButtonBaseProps | 'href'
  >;

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      as = 'button',
      className,
      variant,
      size,
      square,
      glow,
      children,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      buttonVariants({ variant, size, className, square, glow })
    );

    // Extract accessibility props
    const ariaProps = {
      'aria-label': props['aria-label'],
      'aria-labelledby': props['aria-labelledby'],
      'aria-describedby': props['aria-describedby'],
      'aria-pressed': props['aria-pressed'],
      'aria-expanded': props['aria-expanded'],
      'aria-controls': props['aria-controls'],
      'aria-haspopup': props['aria-haspopup'],
      'aria-current': props['aria-current'],
    };

    if (as === 'link') {
      const { href, disabled, ...linkProps } = props as ButtonAsLinkProps & {
        disabled?: boolean;
      };

      // Handle disabled links
      if (disabled) {
        return (
          <button
            aria-disabled="true"
            className={cn(classes, 'pointer-events-none')}
            tabIndex={0}
            type="button"
            {...ariaProps}
          >
            {children}
          </button>
        );
      }

      return (
        <Link
          className={classes}
          href={href}
          ref={ref as Ref<HTMLAnchorElement>}
          {...linkProps}
          {...ariaProps}
        >
          {children}
        </Link>
      );
    }

    const buttonProps = props as ButtonAsButtonProps;

    return (
      <button
        className={classes}
        ref={ref as Ref<HTMLButtonElement>}
        {...buttonProps}
        {...ariaProps}
        // Ensure aria-disabled is set when button is disabled
        aria-disabled={buttonProps.disabled || undefined}
        // Ensure type is set to prevent form submission by default
        type={buttonProps.type || 'button'}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
export type { ButtonProps };
